import type { OwnerMoney } from '@ff/core';
import { signedMoney, money } from '../lib/format.ts';
import { Avatar } from './Avatar.tsx';
import { ManagerLink } from './ManagerLink.tsx';

/**
 * One settlement line in the book. The net figure is the hero: huge, mono,
 * cash-green if you're up and blood-red if you're down. Everything else is
 * quiet around it.
 */
export function LedgerRow({
  rank,
  name,
  team,
  money: m,
  isYou,
}: {
  rank: number;
  name: string;
  team: string;
  money: OwnerMoney;
  isYou?: boolean;
}) {
  // The row already knows the owner id through the money line it renders.
  const up = m.net > 0;
  const even = m.net === 0;
  const tone = even ? 'text-chalk-dim' : up ? 'text-cash' : 'text-blood';

  return (
    <div
      className={`grid grid-cols-[2rem_2.5rem_1fr_auto] items-center gap-3 py-4 sm:gap-4 ${
        isYou ? 'bg-brass/[0.06]' : ''
      }`}
    >
      {/* Standing in the money race. */}
      <div className="font-mono text-sm text-chalk-dim tabular">
        {String(rank).padStart(2, '0')}
      </div>

      <Avatar name={name} size={40} />

      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-display text-lg font-bold leading-tight sm:text-xl">
            <ManagerLink ownerId={m.ownerId}>{name}</ManagerLink>
          </span>
          {isYou && <span className="eyebrow text-brass">you</span>}
        </div>
        <div className="truncate text-xs text-chalk-dim">{team}</div>
      </div>

      <div className="text-right">
        <div className={`font-mono text-2xl font-bold tabular leading-none sm:text-3xl ${tone}`}>
          {signedMoney(m.net)}
        </div>
        <div className="mt-1 font-mono text-[0.7rem] text-chalk-dim tabular">
          won {money(m.earned)}
          {m.atStake > 0 && <span className="text-brass"> · {money(m.atStake)} live</span>}
        </div>
      </div>
    </div>
  );
}
